import React, { useRef } from "react";
import { X, Download } from "lucide-react";
import Button from "@/components/ui/Button";
import html2pdf from "html2pdf.js";

const InvoicePreview = ({ invoice, invoiceData, onClose, onDownload }) => {
  const invoiceRef = useRef(null);

  // Support both prop names for backwards compatibility
  const data = invoice || invoiceData;

  if (!data) {
    return null;
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Map subsidiary code to entity_id
  const subsidiaryToEntityId = {
    puffin_group: 1,
    buildwise: 2,
    laser_shield: 3,
    maz_logistics: 4,
    puffin_energy: 5,
    swingtide: 6,
    puffin_agro: 7,
  };

  // Get entity_id from subsidiary code or use data.entity_id
  const entityId = data.entity_id || subsidiaryToEntityId[data.subsidiary] || 1;

  // Get subsidiary color
  const subsidiaryColors = {
    1: "#001f3f", // Puffin Group - Navy Blue
    2: "#8B4513", // Buildwise - Brown
    3: "#DC143C", // Laser Shield - Crimson Red
    4: "#dc2626", // Maz Logistics - Red
    5: "#0B6623", // Puffin Energy - Dark Green
    6: "#00CED1", // Swingtide - Turquoise/Cyan
    7: "#1B5E20", // Puffin Agro - Darker Forest Green
  };

  const headerColor = subsidiaryColors[entityId] || "#001f3f";
  const items = data.items || [];

  // Dynamic sizing based on number of items - more aggressive
  const itemCount = items.length;
  const isCompact = itemCount > 8;
  const isVeryCompact = itemCount > 15;

  const textSize = isVeryCompact
    ? "text-[9px]"
    : isCompact
      ? "text-[11px]"
      : "text-sm";
  const paddingSize = isVeryCompact
    ? "py-0.5 px-2"
    : isCompact
      ? "py-1 px-2"
      : "py-3 px-4";
  const sectionPadding = isVeryCompact
    ? "px-8 pb-2"
    : isCompact
      ? "px-8 pb-3"
      : "px-8 pb-6";

  // Function to generate PDF from the preview
  const handleGeneratePDF = () => {
    const element = invoiceRef.current;
    const opt = {
      margin: 0,
      filename: `invoice-${data.invoice_number || "draft"}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    html2pdf().set(opt).from(element).save();
  };

  // Get subsidiary logo
  const getSubsidiaryLogo = () => {
    // Check if we have subsidiary data from backend
    if (data.Subsidiary?.logo) {
      return data.Subsidiary.logo;
    }

    // Fallback to entity_id mapping
    const subsidiaryLogos = {
      1: "/icons/puffin_group.png",
      2: "/icons/buildwise.png",
      3: "/icons/laser_shield.png",
      4: "/icons/maz_logistics.png",
      5: "/icons/puffin_energy.png",
      6: "/icons/swingtide.png",
      7: "/icons/puffin_agro.png",
    };

    // Try to get from subsidiary field if available
    if (data.subsidiary) {
      const subsidiaryMap = {
        puffin_group: 1,
        buildwise: 2,
        laser_shield: 3,
        maz_logistics: 4,
        puffin_energy: 5,
        swingtide: 6,
        puffin_agro: 7,
      };
      const entityId = subsidiaryMap[data.subsidiary];
      if (entityId) {
        return subsidiaryLogos[entityId];
      }
    }

    return subsidiaryLogos[data.entity_id] || "/icons/puffin_group.png";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <style>{`
        @media print {
          .page-break-avoid {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .page-break-before {
            page-break-before: always;
            break-before: always;
          }
        }
      `}</style>
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header Actions */}
        <div className="bg-gray-100 px-6 py-3 flex items-center justify-between border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            Invoice Preview - {data.invoice_number}
          </h2>
          <div className="flex items-center space-x-2">
            <Button
              size="sm"
              onClick={() => {
                if (onDownload && data.id) {
                  // If invoice is saved, use backend PDF generation
                  onDownload();
                } else {
                  // Otherwise, generate PDF from preview
                  handleGeneratePDF();
                }
              }}
              className="flex items-center bg-blue-600 hover:bg-blue-700"
            >
              <Download className="h-4 w-4 mr-1" />
              Download PDF
            </Button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Invoice Preview Content */}
        <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
          <div
            ref={invoiceRef}
            className="bg-white shadow-lg mx-auto max-w-4xl"
          >
            {/* Header Section - Split Navy/White */}
            <div style={{ position: "relative" }}>
              <div className="flex" style={{ minHeight: "180px" }}>
                {/* Left half - Navy blue with QR code */}
                <div
                  className="w-1/2 p-8 flex flex-col justify-start"
                  style={{ backgroundColor: headerColor }}
                >
                  <div
                    className="bg-white rounded flex items-center justify-center"
                    style={{ width: "120px", height: "120px" }}
                  >
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent("https://puffingroupltd.com")}`}
                      alt="QR Code"
                      className="w-full h-full"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.parentElement.innerHTML = `<div class="text-center w-full h-full flex items-center justify-center"><div><div class="text-xs font-mono text-gray-500 font-bold">QR CODE</div><div class="text-xs text-gray-400 mt-1">puffingroupltd.com</div></div></div>`;
                      }}
                    />
                  </div>
                </div>

                {/* Right half - White background with logo and INVOICE */}
                <div className="w-1/2 bg-white p-8 flex flex-col items-center justify-center gap-4">
                  <div className="flex items-center gap-3">
                    <img
                      src={getSubsidiaryLogo()}
                      alt="Company Logo"
                      className="h-16 object-contain"
                      onError={(e) => {
                        if (import.meta.env.DEV) {
                          console.log("Logo failed to load:", e.target.src);
                        }
                        e.target.style.display = "none";
                      }}
                      onLoad={(e) => {
                        if (import.meta.env.DEV) {
                          console.log(
                            "Logo loaded successfully:",
                            e.target.src,
                          );
                        }
                      }}
                    />
                    {entityId === 7 && (
                      <p className="font-bold text-2xl text-black">
                        PUFFIN AGRO
                      </p>
                    )}
                  </div>
                  <h1
                    className="font-black"
                    style={{
                      fontSize: "72px",
                      lineHeight: "1",
                      letterSpacing: "0.1em",
                      fontWeight: "900",
                      color: headerColor,
                    }}
                  >
                    INVOICE
                  </h1>
                </div>
              </div>

              {/* Bottom section - Navy blue with Date and Invoice to */}
              <div
                className="flex px-8 pb-6"
                style={{ backgroundColor: headerColor }}
              >
                {/* Left - Date */}
                <div className="w-1/2 text-white">
                  <p className="text-sm font-semibold mb-1">Date:</p>
                  <p className="text-sm">{formatDate(data.invoice_date)}</p>
                </div>

                {/* Right - Invoice to */}
                <div className="w-1/2 text-right">
                  <p className="text-sm font-semibold text-white mb-1">
                    Invoice to:
                  </p>
                  <p className="text-sm text-white font-medium">
                    {data.bill_to_name || data.Customer?.company_name || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Description Section */}
            <div className="px-8 py-6">
              <div
                className="p-4 rounded"
                style={{ backgroundColor: "#e5e7eb" }}
              >
                <p className="text-sm font-semibold text-gray-900 mb-2">
                  Description:
                </p>
                <p className="text-sm text-gray-700">
                  {data.notes || "No description provided"}
                </p>
              </div>
            </div>

            {/* Items Table */}
            <div className={sectionPadding}>
              <table className="w-full">
                <thead>
                  <tr style={{ backgroundColor: headerColor }}>
                    <th
                      className={`text-left text-white font-semibold ${textSize} ${paddingSize}`}
                      style={{ width: "50px" }}
                    >
                      #
                    </th>
                    <th
                      className={`text-left text-white font-semibold ${textSize} ${paddingSize}`}
                    >
                      Item(s) Description
                    </th>
                    <th
                      className={`text-center text-white font-semibold ${textSize} ${paddingSize}`}
                      style={{ width: "100px" }}
                    >
                      Quantity
                    </th>
                    <th
                      className={`text-center text-white font-semibold ${textSize} ${paddingSize}`}
                      style={{ width: "120px" }}
                    >
                      Unit Price
                    </th>
                    <th
                      className={`text-right text-white font-semibold ${textSize} ${paddingSize}`}
                      style={{ width: "150px" }}
                    >
                      Amount (NGN)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.length > 0 ? (
                    items.map((item, index) => (
                      <tr key={index} className="border-b border-gray-200">
                        <td
                          className={`${textSize} text-gray-900 ${paddingSize}`}
                        >
                          {index + 1}
                        </td>
                        <td
                          className={`${textSize} text-gray-900 ${paddingSize}`}
                        >
                          {item.description}
                        </td>
                        <td
                          className={`${textSize} text-gray-900 ${paddingSize} text-center`}
                        >
                          {item.quantity}
                        </td>
                        <td
                          className={`${textSize} text-gray-900 ${paddingSize} text-center`}
                        >
                          {formatCurrency(item.unit_price)}
                        </td>
                        <td
                          className={`${textSize} text-gray-900 ${paddingSize} text-right font-semibold`}
                        >
                          N
                          {formatCurrency(
                            item.line_total || item.quantity * item.unit_price,
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="5"
                        className="text-center py-8 text-gray-500"
                      >
                        No items found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Subtotal, Charges, and Deductions */}
            <div className={`${sectionPadding} page-break-avoid`}>
              <div className="flex justify-end">
                <div className="w-1/2 space-y-2">
                  {/* Subtotal */}
                  <div className="flex justify-between items-center py-3 bg-gray-100 px-4 rounded-lg border-l-4 border-blue-500">
                    <p className="text-base font-bold text-gray-800">
                      Subtotal:
                    </p>
                    <p className="text-lg font-bold text-gray-900">
                      N{formatCurrency(data.subtotal)}
                    </p>
                  </div>

                  {/* Additional Charges */}
                  {data.additional_charges &&
                    data.additional_charges.length > 0 &&
                    data.additional_charges.map((charge, index) => {
                      const amount =
                        charge.type === "percentage"
                          ? (data.subtotal * (parseFloat(charge.amount) || 0)) /
                            100
                          : parseFloat(charge.amount) || 0;
                      return (
                        <div
                          key={index}
                          className="flex justify-between items-center py-2 px-4"
                        >
                          <p className="text-sm text-green-700 font-medium">
                            + {charge.name}{" "}
                            {charge.type === "percentage"
                              ? `(${charge.amount}%)`
                              : ""}
                            :
                          </p>
                          <p className="text-sm text-green-700 font-semibold">
                            N{formatCurrency(amount)}
                          </p>
                        </div>
                      );
                    })}

                  {/* Deductions */}
                  {data.deductions &&
                    data.deductions.length > 0 &&
                    data.deductions.map((deduction, index) => {
                      const amount =
                        deduction.type === "percentage"
                          ? (data.subtotal *
                              (parseFloat(deduction.amount) || 0)) /
                            100
                          : parseFloat(deduction.amount) || 0;
                      return (
                        <div
                          key={index}
                          className="flex justify-between items-center py-2 px-4"
                        >
                          <p className="text-sm text-red-700 font-medium">
                            - {deduction.name}{" "}
                            {deduction.type === "percentage"
                              ? `(${deduction.amount}%)`
                              : ""}
                            :
                          </p>
                          <p className="text-sm text-red-700 font-semibold">
                            N{formatCurrency(amount)}
                          </p>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>

            {/* Grand Total Section */}
            <div className={`${sectionPadding} page-break-avoid`}>
              <div
                className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border-l-4"
                style={{ borderColor: headerColor }}
              >
                <div className="flex justify-between items-center">
                  <p className="text-xl font-bold text-gray-800">Grand Total</p>
                  <p
                    className="text-3xl font-bold"
                    style={{ color: headerColor }}
                  >
                    N{formatCurrency(data.total || data.subtotal)}
                  </p>
                </div>
              </div>
            </div>

            {/* Account Details and Balance Due */}
            <div className={`${sectionPadding} page-break-avoid`}>
              <div className="flex justify-between items-start gap-8">
                <div className="flex-1">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-sm font-bold text-gray-800 mb-3 flex items-center">
                      <span
                        className="w-1 h-4 mr-2"
                        style={{ backgroundColor: headerColor }}
                      ></span>
                      Payment Information
                    </p>
                    {(data.terms_conditions || data.account_details) && (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {data.terms_conditions || data.account_details}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex-shrink-0">
                  <p className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                    Amount Due
                  </p>
                  <div
                    className="rounded-xl shadow-lg p-6 min-w-[240px]"
                    style={{
                      background: `linear-gradient(135deg, ${headerColor} 0%, ${headerColor}dd 100%)`,
                    }}
                  >
                    <p className="text-xs text-blue-100 mb-1 uppercase tracking-wider">
                      Balance Due
                    </p>
                    <p className="text-3xl font-bold text-white">
                      N{formatCurrency(data.balance_due || data.total)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer with blue line */}
            <div
              className={`px-8 ${isVeryCompact ? "pb-2" : isCompact ? "pb-3" : "pb-4"} page-break-avoid`}
            >
              <div
                className="w-full mb-3"
                style={{ borderTop: `2px solid ${headerColor}` }}
              ></div>

              <div className="flex justify-between text-xs text-gray-900">
                {/* Phone */}
                <div className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${headerColor}20` }}
                  >
                    <svg
                      className="w-4 h-4"
                      style={{ color: headerColor }}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                  </div>
                  <div className="whitespace-nowrap">
                    <p className="font-semibold">+234 208 244 4558</p>
                    <p className="font-semibold">+234 707 440 4041</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${headerColor}20` }}
                  >
                    <svg
                      className="w-4 h-4"
                      style={{ color: headerColor }}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold">
                      {data.Subsidiary?.email || "info@puffingroupltd.com"}
                    </p>
                    <p>
                      {data.Subsidiary?.website || "www.puffingroupltd.com"}
                    </p>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${headerColor}20` }}
                  >
                    <svg
                      className="w-4 h-4"
                      style={{ color: headerColor }}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold">
                      No. 21 Mukhtar Mohammad link, Off Ahmadu Bello Way, Kano
                    </p>
                    <p>No. 22 Mambilla Street Aso Drive, Abuja</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePreview;
